import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { ButtonListMenu } from '../components/ButtonListMenu'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { getAppliance, isApplianceType } from '../classes/appliances'
import { ApplianceData, ApplianceSubtype, ApplianceType } from '../classes/appliances/Appliance'
import { unreferencedPortControls } from '../classes/ports/port-availability'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { useEffect, useState } from 'react'
import { useDevices } from '../hooks/useDevices'
import { PortControl } from '../classes/ports/PortControl'
import { ID, NavState, Port } from '../types/models'
import { PortConfig } from '../types/ports'
import { Box, Button } from '@mui/material'
import { ApplianceTypeSelector } from '../components/ApplianceTypeSelector'

export function AddAppliancePage() {
  const { t } = useTranslation()
  const params = useParams()
  const app = useApp()
  const navigate = useNavigate()
  const { control } = useDevices()

  // The session data may still be missing right after a reload: `Initializer` is
  // fetching it. The route guard already sends users without a session to log in.
  const location = app.locations.length ? app.locations[0] : null

  // A port is free for a new appliance when no existing appliance references
  // it (see `unreferencedPortControls`), so the appliance list is needed
  // before any port can be offered. Stays null until every location loaded.
  const [locationAppliances, setLocationAppliances] = useState<ApplianceData[] | null>(null)
  useEffect(() => {
    let cancelled = false
    Promise.all(app.locations.map(loc =>
      callAxios<ApplianceData[]>({ url: `/appliances?location=${loc.id}` })
    )).then(resps => {
      // On any failed fetch keep the ports hidden rather than guess: a port
      // wrongly shown as free lets a second appliance claim it.
      if (!cancelled && resps.every(resp => resp.success)) {
        setLocationAppliances(resps.flatMap(resp => resp.success ? resp.data : []))
      }
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.locations.length])

  // Appliance under inspection.
  const appliance = location && isApplianceType(params.type)
    ? getAppliance({
      name: '',
      location_id: location.id as ID,
      type: params.type as ApplianceType,
      ports: [],
      config: {}
    }, control, app)
    : undefined

  // Way forward recarding subtypes.
  const subtypes: Record<string, string> = appliance ? appliance.getSubtypes() : {}
  let ports: PortControl<Port, PortConfig>[] = []
  if (params.subtype && appliance && locationAppliances) {
    appliance.connectControls(control)
    // Free = referenced by no appliance. NOT judged by the `enabled` hardware
    // config: after an appliance delete the disable-flash confirms only
    // minutes later (never, for an offline device), which made the freed port
    // look permanently occupied here.
    ports = unreferencedPortControls(control.getSuitablePorts(appliance, params.subtype as ApplianceSubtype), locationAppliances)
    ports = appliance.filterSuitablePorts(ports)
  }

  // Ports available.
  const portOptions: Record<string, string> = {}
  const states: Record<string, NavState> = {}
  ports.forEach(portCtrl => {
    portOptions[`${portCtrl.deviceControl.device.hw_id} ${portCtrl.getName()}`] = `/appliances/new/${params.type}/${params.subtype}/${portCtrl.port.id}`
    states[`${portCtrl.deviceControl.device.hw_id} ${portCtrl.getName()}`] = { disable: { port: portCtrl.port.id as ID } }
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (appliance && Object.keys(subtypes).length === 0 && Object.keys(ports).length === 1) {
      navigate(`/appliances/new/${params.type}/${appliance.getDefaultPortType()}/${ports[0].port.id}`, { state: { prev: '/appliances/new' } })
    } else if (appliance && Object.keys(subtypes).length === 0) {
      navigate(`/appliances/new/${params.type}/${appliance.getDefaultPortType()}`, { state: { prev: '/appliances/new' } })
    } else if (appliance && params.subtype !== undefined && Object.keys(ports).length === 1) {
      navigate(`/appliances/new/${params.type}/${params.subtype}/${ports[0].port.id}`, { state: { prev: `/appliances/new/${params.type}` } })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliance && appliance.type, params.type, subtypes.length, Object.keys(ports).length])

  if (!params.type) {
    return (
      <>
        <TopTitle title={t('What Type of Appliance Are You Connecting?')}></TopTitle>
        <ApplianceTypeSelector/>
      </>
    )
  }

  // Unknown type in the URL: start the wizard over instead of throwing from the factory.
  if (!isApplianceType(params.type)) {
    return <Navigate to="/appliances/new" replace/>
  }

  // Wait for the session data.
  if (!appliance) return <></>

  if (!params.subtype) {
    return <>
      <TopTitle title={appliance.getType()}></TopTitle>
      <ButtonListMenu buttons={subtypes} url={`/appliances/new/${params.type}/:id`}/>
    </>
  }

  return <>
    <TopTitle title={t('Select the Connected Port')}></TopTitle>
    <ButtonListMenu buttons={portOptions} states={states}/>
    {locationAppliances !== null && Object.keys(portOptions).length === 0 && (
      <Box>
        <Trans>There are no unused ports available for this purpose.</Trans>
        <Box sx={{ mt: '2rem', textAlign: 'center' }}>
          <Button fullWidth onClick={() => navigate('/appliances/new')} data-cy="button-Continue"><Trans>Cancel</Trans></Button>
        </Box>
      </Box>
    )}
  </>
}
